import React, { useState, useEffect, useRef, useLayoutEffect } from 'react'
import { object, bool } from 'prop-types'
import { createComponent } from '@ycos/fela'
import { SingleColumn } from '@ycos/component-columns'
import { getTags } from '@ycos/coremedia-renderer'
import { LOAD_STATUS } from '@ycos/constants'
import { useUserInfo } from '@ycos/user-information'
import { DefaultLoadingTransition } from './LoadingComponent'

const ACCORDION_LAYOUT_VARIANT = ['isc-accordion']
const FULL_BLEED_LAYOUT_VARIANT = [
  'isc-homepage-category-area',
  'mrp-homepage-servicebanner',
  'mrp-homepage-marketing-3-small',
  'mrp-homepage-marketing-2-large',
  'mrp-homepage-tpromos',
  'isc-category-area'
]

export const CUSTOMER_SEGMENTS_TAG = 'CustomerSegments'

const UserAttributesWrapper = createComponent('UserAttributesWrapper', () => ({
  position: 'relative'
}))

const UserAttributesChildrenWrapper = createComponent('UserAttributesChildrenWrapper', ({ show }) => ({
  opacity: show ? 1 : 0
}))

const withSingleColumn = (component) => (
  <SingleColumn noPaddingTop noPaddingTopMobile item={{ layoutVariant: 'isc-single-column-large' }}>
    {component}
  </SingleColumn>
)

const UserAttributesCollection = ({ children, skipComponent, locale, brand, config }) => {
  if (skipComponent) {
    return children[0].props.children
  }

  const userInfoState = useUserInfo()
  const { userInfo, userInfoStatus } = userInfoState
  const [matchedUserChild, setMatchedUserChild] = useState(null)
  const [boxHeight, setBoxHeight] = useState(0)
  const UserAttributesWrapperEl = useRef(null)
  const [loadStatus, setLoadStatus] = useState(LOAD_STATUS.LOADING)

  const getMatchedChild = async (children, userSegments) => {
    const promises = children.map(async (child) => {
      const tags = getTags(child?.props.item.tags, CUSTOMER_SEGMENTS_TAG)

      const userClassMatches = tags.some((tag) => userSegments.includes(tag))
      const { additionalMatching = () => true } = child?.props.item
      const additionalMatchingResult = await additionalMatching({ userSegments, component: child, locale, brand, config })
      return userClassMatches && additionalMatchingResult
    })

    const results = await Promise.all(promises)
    const matchingComponentIndex = results.findIndex((result) => result === true)
    const matchingChild = children[matchingComponentIndex]

    setMatchedUserChild(matchingChild)
  }

  const isFullbleed = FULL_BLEED_LAYOUT_VARIANT.includes(children[0]?.props?.item?.layoutVariant) || children[0]?.props?.item?.type === 'ynapteaser'
  useEffect(() => {
    async function callGetMatchedChild() {
      // make sure do not use cached data in the localStorage https://git.yoox.net/projects/YOS/repos/ynap-site-furniture/browse/src/services/SFBrowserAPI.js#153-168
      const userSegments = (await window.SF?.getCustomerSegments?.(false)) || []
      await getMatchedChild(children, userSegments)
      setLoadStatus(LOAD_STATUS.LOADED)
    }

    callGetMatchedChild()
  }, [userInfo, userInfoStatus])

  useLayoutEffect(() => {
    if (UserAttributesWrapperEl?.current?.clientHeight > 0) {
      setBoxHeight(UserAttributesWrapperEl.current.clientHeight)
    }
  })

  const LoadingComponent = <DefaultLoadingTransition status={loadStatus} loadingBoxHeight={boxHeight} isFullbleed={isFullbleed} />
  const isAccordion = ACCORDION_LAYOUT_VARIANT.includes(children[0]?.props?.item?.layoutVariant)
  const renderLoadingComponent = (isFullbleed) => (isFullbleed ? LoadingComponent : withSingleColumn(LoadingComponent))

  return (
    <UserAttributesWrapper data-automation="user-attributes-collection" innerRef={UserAttributesWrapperEl}>
      {isAccordion ? null : renderLoadingComponent(isFullbleed)}
      <UserAttributesChildrenWrapper show={boxHeight}>{matchedUserChild || children[0].props.children}</UserAttributesChildrenWrapper>
    </UserAttributesWrapper>
  )
}

UserAttributesCollection.layoutVariant = 'isc-user-attributes-collection'

UserAttributesCollection.propTypes = {
  children: object.isRequired,
  skipComponent: bool
}

UserAttributesCollection.defaultProps = {
  skipComponent: false
}

export default UserAttributesCollection
